import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faPause, faPlay, faPhone, faMobileAlt } from '@fortawesome/free-solid-svg-icons';
import { useBreadcrumb } from '../../../contexts/BreadcrumbContext';
import { checkBGValue, dedupeMeasurementsByDateTimeKeepLatest, getMeasurementValueClass, checkWeightValue, getPresetDateRangeParams, formatDateDisplay } from '../../../utils/measurementUtils';
import measurementService from '../../../services/measurementService';
import patientService from '../../../services/patientService';
import { toast } from 'react-toastify';
import AddBGForm from './AddBGForm';
import AddFollowUpModal from '../../../components/AddFollowUpModal/AddFollowUpModal';
import VitalsTabs from '../../../components/MeasurementData/VitalsTabs/VitalsTabs';
import EmailModal from '../../../components/EmailModal/EmailModal';
import emailService from '../../../services/emailService';
import { printElement } from '../../../utils/reportExportUtils';
import { exportBloodGlucoseTablesPdf } from '../../../utils/measurementTablePdfExport';
import {
  findPeriodByName,
  normalizePeriodName,
  resolvePeriodNameByIdOrLabel,
  resolveDisplayScheduleTargets,
  hasAnyScheduleTargets,
  getMeasurementEpochMs
} from '../../../utils/scheduleTargetUtils';
import {
  PATIENT_SCHEDULE_TARGET_PREF_EVENT,
  getUsePatientScheduleTargetsAtMeasurementMs,
  getUsePatientScheduleTargetsPreference
} from '../../../utils/patientScheduleTargetPreference.js';
import PageLoadingSpinner from '../../../components/PageLoadingSpinner/PageLoadingSpinner';
import './BloodGlucose.css';

const BloodGlucose = ({ practiceId, patientId, practiceIsLocked = false }) => {
  const BG_PERMANENT_START_TIMES = {
    'Wake-up Time': '06:00 AM',
    'Before-Breakfast': '07:01 AM',
    'After-Breakfast': '09:01 AM',
    'Before-Lunch': '11:01 AM',
    'After-Lunch': '01:01 PM',
    'Before-Dinner': '05:01 PM',
    'After-Dinner': '07:01 PM',
    Bedtime: '08:30 PM',
    Midnight: '10:00 PM'
  };
  const { updateBreadcrumbs } = useBreadcrumb();
  const [timer, setTimer] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [queryType, setQueryType] = useState('dateRange');
  const [dateRange, setDateRange] = useState('Last 30 days');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [sortBy, setSortBy] = useState('Weekly average');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [patientData, setPatientData] = useState(null);
  const [measurements, setMeasurements] = useState([]);
  const [scheduleTargets, setScheduleTargets] = useState(null);
  const [scheduleTargetSources, setScheduleTargetSources] = useState(null);
  const [targetTimelineRev, setTargetTimelineRev] = useState(0);
  const [loading, setLoading] = useState(false);
  const [queryLoading, setQueryLoading] = useState(false);
  const [hasFetchedMeasurements, setHasFetchedMeasurements] = useState(false);
  const [hasFetchedPatientData, setHasFetchedPatientData] = useState(false);
  const [hasFetchedScheduleTargets, setHasFetchedScheduleTargets] = useState(false);
  const patientVitals = patientData?.patient?.vitals || 'BP, BG, WT';
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const initialized = useRef(false);

  useEffect(() => {
    if (practiceIsLocked) {
      setShowAddModal(false);
      setShowFollowUpModal(false);
      setIsRunning(false);
      setTimer(0);
    }
  }, [practiceIsLocked]);

  // Fetch patient data and schedule targets on mount
  useEffect(() => {
    if (practiceId && patientId) {
      setHasFetchedPatientData(false);
      setHasFetchedScheduleTargets(false);
      setHasFetchedMeasurements(false);
      setScheduleTargetSources(null);
      if (!initialized.current) {
        if (!practiceIsLocked) {
          setIsRunning(true);
        }
        initialized.current = true;
      }
      fetchPatientData();
      fetchScheduleTargets();
    }
    return () => {
      initialized.current = false;
    };
  }, [practiceId, patientId]);

  // Fetch measurements only on patient/screen load.
  // Query filters are applied explicitly via the Query button.
  useEffect(() => {
    if (practiceId && patientId) {
      fetchMeasurements({ silent: true });
    }
  }, [practiceId, patientId]);

  // Timer effect
  useEffect(() => {
    let interval = null;
    if (isRunning) {
      interval = setInterval(() => {
        setTimer(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isRunning]);

  const formatTimer = (totalSeconds) => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const fetchPatientData = async () => {
    const cacheKey = `measurement:patient:${practiceId}:${patientId}`;
    const cachedRaw = sessionStorage.getItem(cacheKey);
    if (cachedRaw) {
      try {
        const cached = JSON.parse(cachedRaw);
        if (cached && typeof cached === 'object') setPatientData(cached);
      } catch {
        // ignore
      }
    }
    try {
      const response = await patientService.getMeasurementHeaderFast(practiceId, patientId);
      if (response.data.success) {
        const next = response.data.data;
        setPatientData(next);
        sessionStorage.setItem(cacheKey, JSON.stringify(next));
      }
    } catch (error) {
      console.error('Error fetching patient data:', error);
    } finally {
      setHasFetchedPatientData(true);
    }
  };

  const fetchScheduleTargets = async () => {
    try {
      const sources = await patientService.getScheduleTargetSourcesForPatient(practiceId, patientId);
      setScheduleTargetSources(sources);
      const usePatientTargets = getUsePatientScheduleTargetsPreference(practiceId, patientId, sources?.patientData);
      const effective = resolveDisplayScheduleTargets({
        practiceData: sources.practiceData,
        patientData: sources.patientData,
        usePatientTargets
      });
      setScheduleTargets(hasAnyScheduleTargets(effective) ? effective : sources.practiceData);
    } catch (error) {
      console.error('Error fetching schedule targets:', error);
    } finally {
      setHasFetchedScheduleTargets(true);
    }
  };

  useEffect(() => {
    if (!practiceId || !patientId) return undefined;
    const onPref = (ev) => {
      const d = ev?.detail;
      if (String(d?.practiceId) !== String(practiceId) || String(d?.patientId) !== String(patientId)) return;
      setTargetTimelineRev((n) => n + 1);
      fetchScheduleTargets();
    };
    window.addEventListener(PATIENT_SCHEDULE_TARGET_PREF_EVENT, onPref);
    return () => window.removeEventListener(PATIENT_SCHEDULE_TARGET_PREF_EVENT, onPref);
  }, [practiceId, patientId]);

  const fetchMeasurements = async ({ silent = false, bypassCache = false } = {}) => {
    try {
      let fromDateParam, toDateParam;

      if (queryType === 'dateRange') {
        const params = getPresetDateRangeParams(dateRange);
        fromDateParam = params.fromDateParam;
        toDateParam = params.toDateParam;
      } else {
        // Inputs are already YYYY-MM-DD from type="date"
        if (fromDate && toDate) {
          fromDateParam = fromDate;
          toDateParam = toDate;
        }
      }

      if (fromDateParam && toDateParam) {
        const queryCacheKey = `measurement:bg:${practiceId}:${patientId}:${queryType}:${dateRange}:${fromDateParam}:${toDateParam}:${sortBy}`;
        let hadCachedData = false;
        if (!bypassCache) {
          const cachedRaw = sessionStorage.getItem(queryCacheKey);
          if (cachedRaw) {
            try {
              const cached = JSON.parse(cachedRaw);
              if (Array.isArray(cached)) {
                setMeasurements(cached);
                setHasFetchedMeasurements(true);
                hadCachedData = true;
              }
            } catch {
              // ignore
            }
          }
        }
        if (!silent || !hadCachedData) {
          setQueryLoading(true);
        }
        const sortKeyMap = {
          '1 Day Average': 'daily_average',
          'Weekly average': 'weekly_average',
          '14 Days Average': 'weekly_average',
          'Monthly average': 'monthly_average',
          '3 Months Average': 'monthly_average',
          'All': 'date',
          'Daily average': 'daily_average'
        };
        const response = await measurementService.getBloodGlucoseByDateRange(
          practiceId,
          patientId,
          fromDateParam,
          toDateParam,
          sortKeyMap[sortBy] || 'weekly_average'
        );

        if (response.data?.success) {
          const next = response.data.data || [];
          setMeasurements((prev) => (JSON.stringify(prev || []) === JSON.stringify(next) ? prev : next));
          sessionStorage.setItem(queryCacheKey, JSON.stringify(next));
          setLoading(false);
        }
      }
    } catch (error) {
      console.error('Error fetching measurements:', error);
      if (error.response?.status === 404) {
        console.log('Measurement API endpoint not yet available');
        setMeasurements([]);
      } else {
        toast.error('Failed to load measurements');
        setMeasurements([]);
      }
    } finally {
      setQueryLoading(false);
      setLoading(false);
      setHasFetchedMeasurements(true);
    }
  };

  const handleQuery = () => {
    if (queryType === 'from' && (!fromDate || !toDate)) {
      toast.warning('Please select both start and end dates');
      return;
    }
    fetchMeasurements({ silent: false, bypassCache: true });
  };

  const handleAddDataSuccess = () => {
    try {
      Object.keys(sessionStorage).forEach((key) => {
        if (key.startsWith(`measurement:bg:${practiceId}:${patientId}`)) {
          sessionStorage.removeItem(key);
        }
      });
    } catch {
      // ignore
    }
    fetchMeasurements({ silent: false, bypassCache: true });
  };

  const formatDate = (dateStr) => formatDateDisplay(dateStr);

  const formatDateIso = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const BG_CANONICAL_PERIODS = [
    'Wake-up Time',
    'Before-Breakfast',
    'After-Breakfast',
    'Before-Lunch',
    'After-Lunch',
    'Before-Dinner',
    'After-Dinner',
    'Bedtime',
    'Midnight'
  ];

  const mapToCanonicalBGPeriod = (name) => {
    const normalized = normalizePeriodName(name);
    if (!normalized) return null;
    return BG_CANONICAL_PERIODS.find((p) => normalizePeriodName(p) === normalized) || null;
  };

  const convertTimeToMinutes = (timeStr) => {
    if (!timeStr) return null;
    if (timeStr.includes('AM') || timeStr.includes('PM')) {
      const [time, meridiem] = timeStr.trim().split(' ');
      const [hh, mm] = time.split(':').map(Number);
      if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
      let hour = hh;
      if (meridiem?.toUpperCase() === 'PM' && hour !== 12) hour += 12;
      if (meridiem?.toUpperCase() === 'AM' && hour === 12) hour = 0;
      return hour * 60 + mm;
    }
    const [h, m] = timeStr.split(':').map(Number);
    if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
    return h * 60 + m;
  };

  const resolveTargetsBundleAtMs = useCallback(
    (ms) => {
      if (!scheduleTargetSources) return null;
      const usePatient = getUsePatientScheduleTargetsAtMeasurementMs(
        practiceId,
        patientId,
        ms,
        scheduleTargetSources.patientData
      );
      return resolveDisplayScheduleTargets({
        practiceData: scheduleTargetSources.practiceData,
        patientData: scheduleTargetSources.patientData,
        usePatientTargets: usePatient
      });
    },
    [scheduleTargetSources, practiceId, patientId, targetTimelineRev]
  );

  const resolvePeriodNameByTime = (measureTime, configuredOverride) => {
    const configured = Array.isArray(configuredOverride)
      ? configuredOverride
      : Array.isArray(scheduleTargets?.bloodGlucoseTimePeriod)
        ? scheduleTargets.bloodGlucoseTimePeriod
        : [];
    const periods =
      configured.length > 0
        ? configured
        : [
            { name: 'Before-Breakfast', startTime: '12:00 AM', endTime: '11:59 AM' },
            { name: 'After-Lunch', startTime: '12:00 PM', endTime: '5:59 PM' },
            { name: 'Bedtime', startTime: '6:00 PM', endTime: '11:59 PM' }
          ];
    const current = convertTimeToMinutes(measureTime);
    if (current === null) return null;

    const matches = periods
      .map((period) => {
        const start = convertTimeToMinutes(period.startTime || period.start);
        const end = convertTimeToMinutes(period.endTime || period.end);
        if (start === null || end === null) return null;

        const isOvernight = end < start;
        const inRange = isOvernight
          ? current >= start || current <= end
          : current >= start && current <= end;
        if (!inRange) return null;

        const duration = isOvernight ? (1440 - start + end) : (end - start);
        return { name: period.name, isOvernight, duration };
      })
      .filter(Boolean);

    if (matches.length === 0) return null;

    matches.sort((a, b) => {
      if (a.isOvernight !== b.isOvernight) return a.isOvernight ? 1 : -1;
      return a.duration - b.duration;
    });

    return matches[0]?.name || null;
  };

  // Determine period: prefer explicit labels from the device/API (period_name, measure_note, note)
  // so rows land in the intended column. Time-window matching is only a fallback when no label maps.
  const determinePeriodFromMeasurement = (measurement) => {
    const ms = getMeasurementEpochMs(measurement);
    const bundle = resolveTargetsBundleAtMs(ms) || scheduleTargets;
    const configuredPeriods = Array.isArray(bundle?.bloodGlucoseTimePeriod) ? bundle.bloodGlucoseTimePeriod : [];

    const normalizeToConfiguredPeriod = (value) => {
      if (!value) return null;
      const normalized = normalizePeriodName(value);
      const direct = periodNames.find((p) => normalizePeriodName(p) === normalized);
      if (direct) return direct;
      const canonical = mapToCanonicalBGPeriod(value);
      if (!canonical) return null;
      return periodNames.find((p) => normalizePeriodName(p) === normalizePeriodName(canonical)) || canonical;
    };

    const labelForResolve =
      measurement.period_name ||
      measurement.period ||
      measurement.measure_note ||
      measurement.note ||
      '';

    const storedPeriodName = labelForResolve
      ? resolvePeriodNameByIdOrLabel(configuredPeriods, {
          ...measurement,
          period_name: labelForResolve
        })
      : resolvePeriodNameByIdOrLabel(configuredPeriods, measurement);

    const resolvedStored = normalizeToConfiguredPeriod(storedPeriodName || labelForResolve);
    if (resolvedStored) return resolvedStored;

    const measureTime =
      measurement.measurement_time ||
      measurement.measure_new_date_time?.split('T')[1]?.slice(0, 5) ||
      measurement.measure_date_time?.split(' ')[1]?.slice(0, 5) ||
      '';

    if (measureTime) {
      const byTime = resolvePeriodNameByTime(measureTime, configuredPeriods);
      const resolvedByTime = normalizeToConfiguredPeriod(byTime);
      if (resolvedByTime) return resolvedByTime;
    }

    return null;
  };

  // Get period names from schedule targets
  const periodNames = useMemo(() => {
    const names = Array.isArray(scheduleTargets?.bloodGlucoseTimePeriod)
      ? scheduleTargets.bloodGlucoseTimePeriod.map((p) => p?.name).filter(Boolean)
      : [];
    const source = names.length > 0 ? names : BG_CANONICAL_PERIODS;
    const seen = new Set();
    return source.filter((name) => {
      const key = normalizePeriodName(name);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [scheduleTargets]);

  const getPeriodColor = () => '#DEF3F5';

  // Get period info (including time ranges) from schedule targets
  const getPeriodInfo = (periodName) => {
    if (!scheduleTargets?.bloodGlucoseTimePeriod) {
      return null;
    }
    
    // Map daily table period names to schedule target period names
    const searchName = mapToCanonicalBGPeriod(periodName) || periodName;
    const period = findPeriodByName(scheduleTargets.bloodGlucoseTimePeriod, searchName);
    
    if (!period) return null;
    
    // Get time and convert to 12-hour format for display
    const fixedStartTime = BG_PERMANENT_START_TIMES[period.name] || BG_PERMANENT_START_TIMES[searchName] || '';
    const startTime = fixedStartTime || period.startTime || period.start || '';
    const endTime = period.endTime || period.end || '';
    
    // Format time for display in 12-hour format
    const formatTimeDisplay = (time) => {
      if (!time) return '';
      // If already in 12-hour format, return as is
      if (time.includes('AM') || time.includes('PM')) return time;
      // Convert from 24-hour format to 12-hour format
      const [h, m] = time.split(':');
      const hour = parseInt(h);
      const period = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      const minutes = m || '00';
      return `${displayHour}:${minutes} ${period}`;
    };
    
    return {
      name: period.name,
      startTime: startTime,
      endTime: endTime,
      startTimeDisplay: formatTimeDisplay(startTime),
      endTimeDisplay: formatTimeDisplay(endTime)
    };
  };

  // Get target for a period
  const getTargetForPeriod = (periodName, bundleOverride) => {
    const bundle = bundleOverride || scheduleTargets;
    if (!bundle?.bloodGlucoseTargetRange) return { min: 60, max: 110 };
    const targets = bundle.bloodGlucoseTargetRange;
    const normalizedPeriod = normalizePeriodName(periodName);
    
    // Handle Before-Meal and After-Meal targets
    if (
      normalizedPeriod === normalizePeriodName('Before-Meal') ||
      ['beforebreakfast', 'beforelunch', 'beforedinner'].includes(normalizedPeriod)
    ) {
      const target = targets.find(t => 
        normalizePeriodName(t.name).includes('beforemeal')
      ) || targets[0] || { min: 60, max: 110 };
      return {
        min: target.minimum || target.min || 60,
        max: target.maximum || target.max || 110
      };
    }
    
    if (
      normalizedPeriod === normalizePeriodName('After-Meal') ||
      ['afterbreakfast', 'afterlunch', 'afterdinner'].includes(normalizedPeriod)
    ) {
      const target = targets.find(t => 
        normalizePeriodName(t.name).includes('aftermeal')
      ) || targets[0] || { min: 70, max: 140 };
      return {
        min: target.minimum || target.min || 70,
        max: target.maximum || target.max || 140
      };
    }
    
    // Find target by period name or use first as default
    const period = bundle.bloodGlucoseTimePeriod?.find(
      (p) => normalizePeriodName(p.name) === normalizedPeriod
    );
    const target = targets.find(
      t =>
        normalizePeriodName(t.name) === normalizedPeriod ||
        normalizePeriodName(t.name).includes(normalizedPeriod) ||
        normalizedPeriod.includes(normalizePeriodName(t.name)) ||
        normalizePeriodName(t.periodName) === normalizedPeriod ||
        t.id === period?.id
    ) || targets[0] || { min: 60, max: 110 };
    
    return {
      min: target.minimum || target.min || 60,
      max: target.maximum || target.max || 110
    };
  };

  const effectiveMeasurements = useMemo(
    () => dedupeMeasurementsByDateTimeKeepLatest(measurements),
    [measurements]
  );

  const awaitingTargetsForColoring = useMemo(
    () =>
      effectiveMeasurements.length > 0 &&
      (!hasFetchedScheduleTargets || scheduleTargetSources == null || !hasFetchedMeasurements),
    [
      effectiveMeasurements.length,
      hasFetchedScheduleTargets,
      scheduleTargetSources,
      hasFetchedMeasurements
    ]
  );

  // Group measurements by date
  const groupedMeasurements = useMemo(() => {
    const grouped = {};
    
    const sortedMeasurements = [...effectiveMeasurements].sort((a, b) => {
      const dateA = a.measurement_date || a.measure_new_date_time?.split('T')[0] || a.created_at?.split('T')[0] || '';
      const dateB = b.measurement_date || b.measure_new_date_time?.split('T')[0] || b.created_at?.split('T')[0] || '';
      
      if (dateA !== dateB) {
        return new Date(dateB) - new Date(dateA);
      }
      
      const timeA = a.measurement_time || 
                   a.measure_new_date_time?.split('T')[1] || 
                   a.measure_date_time?.split(' ')[1] || 
                   a.created_at?.split('T')[1] || 
                   '00:00:00';
      const timeB = b.measurement_time || 
                   b.measure_new_date_time?.split('T')[1] || 
                   b.measure_date_time?.split(' ')[1] || 
                   b.created_at?.split('T')[1] || 
                   '00:00:00';
      return timeB.localeCompare(timeA);
    });

    // Deduplicate exact duplicates only:
    // same date+time + same glucose values => keep latest.
    // same date+time + different glucose values => keep both.
    const uniqueByDateTime = new Map();
    sortedMeasurements.forEach((m) => {
      const date = m.measurement_date || m.measure_new_date_time?.split('T')[0] || m.created_at?.split('T')[0] || '';
      const time = m.measurement_time ||
        m.measure_new_date_time?.split('T')[1]?.slice(0, 5) ||
        m.measure_date_time?.split(' ')[1]?.slice(0, 5) || '';
      const key = `${date}|${time}|${m.blood_glucose_value_1 ?? ''}|${m.blood_glucose_value_2 ?? ''}|${m.blood_glucose_value_3 ?? ''}|${m.blood_glucose_value ?? ''}`;
      if (!uniqueByDateTime.has(key)) {
        uniqueByDateTime.set(key, m);
      }
    });
    
    Array.from(uniqueByDateTime.values()).forEach(measurement => {
      const date = measurement.measurement_date || measurement.measure_new_date_time?.split('T')[0] || measurement.created_at?.split('T')[0];
      
      if (!grouped[date]) {
        grouped[date] = [];
      }
      
      grouped[date].push(measurement);
    });
    
    return grouped;
  }, [effectiveMeasurements]);

  const sortedDates = useMemo(() => {
    return Object.keys(groupedMeasurements).sort((a, b) => new Date(b) - new Date(a));
  }, [groupedMeasurements]);

  const dateBuckets = useMemo(() => {
    if (sortedDates.length === 0) return [];

    if (sortBy === 'All') {
      return [{ id: 0, label: 'All Dates', dates: sortedDates }];
    }

    const daysPerBucket = {
      '1 Day Average': 1,
      'Weekly average': 7,
      '14 Days Average': 14,
      'Monthly average': 30,
      '3 Months Average': 90,
      'Daily average': 1
    }[sortBy] || 7;

    const newest = new Date(sortedDates[0]);
    newest.setHours(0, 0, 0, 0);
    const msPerDay = 24 * 60 * 60 * 1000;

    const buckets = new Map();
    sortedDates.forEach((dateStr) => {
      const d = new Date(dateStr);
      d.setHours(0, 0, 0, 0);
      const diffDays = Math.floor((newest - d) / msPerDay);
      const idx = Math.floor(diffDays / daysPerBucket);
      if (!buckets.has(idx)) buckets.set(idx, []);
      buckets.get(idx).push(dateStr);
    });

    return Array.from(buckets.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([idx, dates]) => {
        const sorted = [...dates].sort((a, b) => new Date(b) - new Date(a));
        const start = sorted[sorted.length - 1];
        const end = sorted[0];
        return {
          id: idx,
          label: `Date Range : ${formatDateIso(start)} ~ ${formatDateIso(end)}`,
          dates: sorted
        };
      });
  }, [sortedDates, sortBy]);

  // Calculate statistics for a period
  const calculatePeriodStats = (allMeasurements, periodName) => {
    const normalizedPeriod = normalizePeriodName(periodName);
    const periodMeasurements = allMeasurements.filter((m) => {
      const resolved = determinePeriodFromMeasurement(m);
      return resolved && normalizePeriodName(resolved) === normalizedPeriod;
    });

    if (!periodMeasurements || periodMeasurements.length === 0) {
      return { avg: null, max: null, min: null, count: 0, above: 0, below: 0, within: 0 };
    }

    const bgValues = periodMeasurements
      .map(m => parseFloat(m.blood_glucose_value_1 || m.blood_glucose_value))
      .filter(v => !isNaN(v));
    
    let above = 0, below = 0, within = 0;
    periodMeasurements.forEach(m => {
      const bgValue = m.blood_glucose_value_1 || m.blood_glucose_value;
      const bundle = resolveTargetsBundleAtMs(getMeasurementEpochMs(m)) || scheduleTargets;
      const target = getTargetForPeriod(periodName, bundle);
      const status = checkBGValue(bgValue, target);
      if (status === 'high') above++;
      else if (status === 'low') below++;
      else within++;
    });

    const bgAvg = bgValues.length > 0 ? bgValues.reduce((a, b) => a + b, 0) / bgValues.length : null;
    const bgMax = bgValues.length > 0 ? Math.max(...bgValues) : null;
    const bgMin = bgValues.length > 0 ? Math.min(...bgValues) : null;

    return {
      avg: bgAvg,
      max: bgMax,
      min: bgMin,
      count: periodMeasurements.length,
      above,
      below,
      within
    };
  };

  // Render all readings for one period/date inside same cells
  const renderPeriodMeasurementsCells = (periodMeasurements, periodName) => {
    if (!periodMeasurements || periodMeasurements.length === 0) {
      return (
        <>
          <td></td>
          <td></td>
        </>
      );
    }
    
    // Map daily table period names to target period names
    let targetPeriodName = periodName;
    if (periodName === 'Wake-up Time') targetPeriodName = 'Wake-Up';
    else if (periodName === 'Before-Breakfast') targetPeriodName = 'Before-Meal';
    else if (periodName === 'After-Breakfast') targetPeriodName = 'After-Meal';
    else if (periodName === 'Before-Lunch') targetPeriodName = 'Before-Meal';
    else if (periodName === 'After-Lunch') targetPeriodName = 'After-Meal';
    else if (periodName === 'Before-Dinner') targetPeriodName = 'Before-Meal';
    else if (periodName === 'After-Dinner') targetPeriodName = 'After-Meal';
    
    const readings = [...periodMeasurements].sort((a, b) => {
      const timeA = a.measurement_time ||
        a.measure_new_date_time?.split('T')[1] ||
        a.measure_date_time?.split(' ')[1] ||
        a.created_at?.split('T')[1] ||
        '00:00:00';
      const timeB = b.measurement_time ||
        b.measure_new_date_time?.split('T')[1] ||
        b.measure_date_time?.split(' ')[1] ||
        b.created_at?.split('T')[1] ||
        '00:00:00';
      return timeB.localeCompare(timeA);
    });

    return (
      <>
        <td>
          <div className="bg-cell-stack">
            {readings.map((measurement, index) => {
              const bgValue = measurement.blood_glucose_value_1 || measurement.blood_glucose_value;
              if (bgValue == null || bgValue === '') return <div key={`${measurement.id || index}-bg`}></div>;
              const bundle =
                resolveTargetsBundleAtMs(getMeasurementEpochMs(measurement)) || scheduleTargets;
              const target = getTargetForPeriod(targetPeriodName, bundle);
              const status = checkBGValue(bgValue, target);
              const cellClass = getMeasurementValueClass(status);
              return (
                <div key={`${measurement.id || index}-bg`} className={cellClass}>
                  {Math.round(parseFloat(bgValue))}
                </div>
              );
            })}
          </div>
        </td>
        <td>
          <div className="bg-cell-stack">
            {readings.map((measurement, index) => {
              const measureTime = measurement.measurement_time ||
                measurement.measure_new_date_time?.split('T')[1]?.slice(0, 5) ||
                measurement.measure_date_time?.split(' ')[1]?.slice(0, 5) ||
                '';
              const formattedTime = measureTime ? formatTime(measureTime) : '';
              return <div key={`${measurement.id || index}-time`} className="bg-time-text">{formattedTime}</div>;
            })}
          </div>
        </td>
      </>
    );
  };

  const calculateOverallTargetStats = () => {
    const stats = { above: 0, below: 0, within: 0 };
    effectiveMeasurements.forEach((m) => {
      const period = determinePeriodFromMeasurement(m);
      const bundle = resolveTargetsBundleAtMs(getMeasurementEpochMs(m)) || scheduleTargets;
      const target = getTargetForPeriod(period, bundle);
      const raw = m.blood_glucose_value_1 ?? m.blood_glucose_value;
      const value = raw != null ? Number(raw) : null;
      if (value == null || Number.isNaN(value)) return;
      const status = checkBGValue(value, target);
      if (status === 'high') stats.above += 1;
      else if (status === 'low') stats.below += 1;
      else stats.within += 1;
    });
    return stats;
  };

  const overallStats = calculateOverallTargetStats();
  const totalReadingCount = overallStats.above + overallStats.below + overallStats.within;

  const formatPercentCount = (count, total) => {
    if (!total) return `0% (${count})`;
    const pct = Math.round((count / total) * 100);
    return `${pct}% (${count})`;
  };

  const getSummaryPeriodMeta = (periodName) => {
    const key = normalizePeriodName(periodName);
    if (key.includes('wakeup')) return { group: 'Wake-up', sub: null, isSingleHeader: true };
    if (key.includes('beforebreakfast')) return { group: 'Breakfast', sub: 'Before-Meal' };
    if (key.includes('afterbreakfast')) return { group: 'Breakfast', sub: 'After-Meal' };
    if (key.includes('beforelunch')) return { group: 'Lunch', sub: 'Before-Meal' };
    if (key.includes('afterlunch')) return { group: 'Lunch', sub: 'After-Meal' };
    if (key.includes('beforedinner')) return { group: 'Dinner', sub: 'Before-Meal' };
    if (key.includes('afterdinner')) return { group: 'Dinner', sub: 'After-Meal' };
    if (key.includes('bedtime')) return { group: 'Bedtime', sub: null, isSingleHeader: true };
    if (key.includes('midnight')) return { group: 'Midnight', sub: null, isSingleHeader: true };
    return { group: periodName, sub: periodName, isSingleHeader: false };
  };

  const summaryPeriodColumns = useMemo(
    () => periodNames.map((periodName) => ({ periodName, ...getSummaryPeriodMeta(periodName) })),
    [periodNames]
  );

  const summaryHeaderGroups = useMemo(() => {
    const groups = [];
    summaryPeriodColumns.forEach((meta) => {
      const existing = groups.find((g) => g.group === meta.group);
      const item = { periodName: meta.periodName, sub: meta.sub, isSingleHeader: !!meta.isSingleHeader };
      if (existing) existing.items.push(item);
      else groups.push({ group: meta.group, items: [item] });
    });
    return groups;
  }, [summaryPeriodColumns]);

  const totalRowLayout = useMemo(() => {
    const beforeMealCols = summaryPeriodColumns.filter((col) => col.sub === 'Before-Meal').length;
    const afterMealCols = summaryPeriodColumns.filter((col) => col.sub === 'After-Meal').length;
    const remainingCols = Math.max(summaryPeriodColumns.length - beforeMealCols - afterMealCols, 0);
    return {
      beforeMealCols: Math.max(beforeMealCols, 1),
      afterMealCols: Math.max(afterMealCols, 1),
      remainingCols: Math.max(remainingCols, 1)
    };
  }, [summaryPeriodColumns]);

  const rangeTotals = useMemo(
    () => ({
      above: overallStats.above,
      below: overallStats.below,
      within: overallStats.within
    }),
    [overallStats]
  );

  // Get patient info for display
  const patient = patientData?.patient;
  const patientInfo = useMemo(() => {
    const patientNameLine = patient ? `${patient.last_name || ''}, ${patient.first_name || ''}`.trim() : 'Loading...';
    const patientDOB = patient?.date_of_birth
      ? `${formatDate(patient.date_of_birth)} (${new Date().getFullYear() - new Date(patient.date_of_birth).getFullYear()} years)`
      : 'N/A';
    const patientPhone = patient?.phone_number || 'N/A';
    const patientDevice = patientData?.device_serial || 'N/A';
    return { patientNameLine, patientDOB, patientPhone, patientDevice };
  }, [patient, patientData?.device_serial]);
  const { patientNameLine, patientDOB, patientPhone, patientDevice } = patientInfo;

  // Set breadcrumbs (must be after patientName is declared)
  useEffect(() => {
    if (patientData) {
      const resolvedPracticeName =
        patientData?.patient?.practice_name ||
        patientData?.practice?.practice_name ||
        patientData?.practice_name ||
        '';
      const practiceLabel = String(resolvedPracticeName).trim() || 'Practice';
      updateBreadcrumbs([
        { label: 'Practices', path: '/practices', active: false },
        { label: practiceLabel, path: `/practices/${practiceId}`, active: false },
        { label: 'Patients', path: `/practices/${practiceId}?tab=patient`, active: false },
        { label: patientNameLine, path: `/practices/${practiceId}/patients/${patientId}`, active: false },
        { label: 'Summary Of Measurement', path: null, active: true }
      ]);
    }
  }, [patientData, practiceId, patientId, patientNameLine, updateBreadcrumbs]);

  const reportTitle = `Blood Glucose Report - ${patientNameLine}`;
  const exportFilename = `BG-Report-${patientNameLine.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`;

  const handlePrint = () => {
    try {
      const el = document.getElementById('bg-report-content');
      printElement(el, reportTitle);
    } catch (e) {
      toast.error(e?.message || 'Print failed');
    }
  };

  const handleExport = () => {
    if (effectiveMeasurements.length === 0) {
      toast.warning('No data to export');
      return;
    }
    try {
      exportBloodGlucoseTablesPdf({
        filename: exportFilename,
        title: reportTitle,
        patient: {
          name: patientNameLine,
          dob: patientDOB,
          phone: patientPhone,
          device: patientDevice
        },
        dateBuckets,
        periodNames,
        groupedMeasurements,
        determinePeriodFromMeasurement,
        summaryPeriodColumns,
        calculatePeriodStats,
        effectiveMeasurements,
        rangeTotals,
        totalReadingCount,
        formatPercentCount,
        totalRowLayout,
        formatDate,
        formatTime
      });
      toast.success('Exported to PDF');
    } catch (e) {
      toast.error(e?.message || 'Export failed');
    }
  };

  const defaultEmailSubject = `Blood Glucose Report - ${patientNameLine} (${new Date().toLocaleDateString()})`;
  const defaultEmailContent = `Blood Glucose Report\nPatient: ${patientNameLine} (DOB: ${patientDOB})\nGenerated: ${new Date().toLocaleString()}\nTotal Readings: ${effectiveMeasurements.length}`;

  return (
    <div className="blood-glucose-page">
      {practiceIsLocked && (
        <div className="practice-locked-measurement-banner" role="status">
          This practice is locked. Historical readings remain available for up to 30 days. New measurements and
          follow-ups are not allowed.
        </div>
      )}
      <div id="bg-report-content" style={{ padding: '0px' }}>
      <>
      
      <div className="bg-layout-grid">
        <h4 className="bg-layout-title" data-export-ignore="true">Blood Glucose Measurement Controls</h4>
        <div className="bg-layout-top-row">
          {!practiceIsLocked && (
            <div className="bg-timer-wrapper" data-export-ignore="true">
               <div className="bg-timer-box">
                  <span className="bg-timer-value">{formatTimer(timer)}</span>
               </div>
               <button
                  type="button"
                  className="bg-timer-pause-btn"
                  onClick={() => setIsRunning(prev => !prev)}
                  title={isRunning ? "Pause Timer" : "Start Timer"}
               >
                  {isRunning ? (
                    <div className="bg-pause-icon"><span>|</span><span>|</span></div>
                  ) : (
                    <FontAwesomeIcon icon={faPlay} className="bg-play-icon" />
                  )}
               </button>
            </div>
          )}

          <div className="bg-patient-details-grid">
            <div className="bg-info-item">
              <span className="bg-label">Name :</span>
              <span className="bg-value">{patientNameLine}</span>
            </div>
            <div className="bg-info-item">
              <span className="bg-label">DOB :</span>
              <span className="bg-value">{patientDOB}</span>
            </div>
            <div className="bg-info-item">
              <span className="bg-label">Phone :</span>
              <span className="bg-value">{patientPhone}</span>
            </div>
            <div className="bg-info-item">
              <span className="bg-label">Device :</span>
              <span className="bg-value">{patientDevice}</span>
            </div>
          </div>
        </div>

        <div className="bg-layout-middle-row" data-export-ignore="true">
          <div className="bg-middle-left">
            <VitalsTabs
              practiceId={practiceId}
              patientId={patientId}
              vitals={patientVitals}
              active="blood-glucose"
              mode="measurement"
            />
          </div>
          {!practiceIsLocked && (
            <div className="bg-middle-right">
              <button className="bg-add-followup-btn" type="button" onClick={() => setShowFollowUpModal(true)}>
                Add Follow Up
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Query Section */}
      <div className="bg-outline-section bg-query-box-custom" data-export-ignore="true">
        <h4 className="bg-outline-title bg-query-title-custom">Query Period</h4>
        <div className="bg-query-body">
          <div className="bg-query-left">
            <div className="bg-radio-row bg-query-row-first">
              <label className="bg-radio-label">
                <input
                  type="radio"
                  name="queryType"
                  value="dateRange"
                  checked={queryType === 'dateRange'}
                  onChange={(e) => setQueryType(e.target.value)}
                />
                <span style={{ fontWeight: 600, minWidth: '80px' }}>Date Range</span>
              </label>
              <select
                className="bg-dropdown"
                style={{ marginLeft: '20px', flex: 1 }}
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                disabled={queryType !== 'dateRange'}
              >
                <option>Last 30 days</option>
                <option>Last 7 days</option>
                <option>Last 14 days</option>
                <option>Last 60 days</option>
                <option>Last 90 days</option>
              </select>
            </div>
            
            <div className="bg-radio-row" style={{ marginTop: '20px' }}>
              <label className="bg-radio-label">
                <input
                  type="radio"
                  name="queryType"
                  value="from"
                  checked={queryType === 'from'}
                  onChange={(e) => setQueryType(e.target.value)}
                />
                <span style={{ fontWeight: 600, minWidth: '80px' }}>From</span>
              </label>
              <div className="bg-date-inputs" style={{ marginLeft: '20px', flex: 1, display: 'flex', alignItems: 'center' }}>
                <input
                  type="date"
                  className="bg-date-input"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  onClick={(e) => e.target.showPicker && e.target.showPicker()}
                  disabled={queryType !== 'from'}
                />
                <span className="bg-date-separator" style={{ margin: '0 15px', fontWeight: 600 }}>To</span>
                <input
                  type="date"
                  className="bg-date-input"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  onClick={(e) => e.target.showPicker && e.target.showPicker()}
                  disabled={queryType !== 'from'}
                />
              </div>
            </div>
          </div>
          
          <div className="bg-query-right" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <div className="bg-radio-row bg-query-row-first">
              <div style={{ fontWeight: 600, minWidth: '85px' }}>Sort Data by</div>
              <select
                className="bg-dropdown"
                style={{ marginLeft: '20px', flex: 1 }}
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option>1 Day Average</option>
                <option>Weekly average</option>
                <option>14 Days Average</option>
                <option>Monthly average</option>
                <option>3 Months Average</option>
                <option>All</option>
              </select>
            </div>
            
            <div className="bg-action-buttons-row">
              <button className="bg-action-btn" onClick={handleQuery}>
                Query
              </button>
              {!practiceIsLocked && (
                <button className="bg-action-btn" onClick={() => setShowAddModal(true)}>Add Data</button>
              )}
              <button className="bg-action-btn" onClick={handlePrint}>Print</button>
              <button className="bg-action-btn" onClick={handleExport}>Export</button>
              <button className="bg-action-btn" onClick={() => setShowEmailModal(true)}>Email</button>
            </div>
          </div>
        </div>
      </div>

      {/* Daily Measurement Log Table */}
      <div className="bg-outline-section">
        <h4 className="bg-outline-title">Blood Glucose</h4>
        {queryLoading || awaitingTargetsForColoring ? (
          <PageLoadingSpinner minHeight="280px" />
        ) : (
        <>
        {dateBuckets.length > 0 ? (
          dateBuckets.map((bucket) => {
            const dateMeasurementsByPeriodMap = {};
            bucket.dates.forEach((date) => {
              const dateMeasurements = groupedMeasurements[date] || [];
              const perPeriod = {};
              dateMeasurements.forEach((m) => {
                const period = determinePeriodFromMeasurement(m);
                const matchedPeriod = periodNames.find(
                  (p) => normalizePeriodName(p) === normalizePeriodName(period || '')
                );
                if (!matchedPeriod) return;
                if (!perPeriod[matchedPeriod]) perPeriod[matchedPeriod] = [];
                perPeriod[matchedPeriod].push(m);
              });
              dateMeasurementsByPeriodMap[date] = perPeriod;
            });

            return (
              <div className="bg-measurement-table-section bg-section-table" key={`bucket-${bucket.id}`}>
                <div className="bg-time-text" style={{ padding: '4px 10px', fontWeight: 500, color: '#2c2c2c', fontSize: '12px' }}>{bucket.label}</div>
                <table className="bg-measurement-table">
                  <thead>
                    <tr>
                      <th rowSpan="2" className="bg-table-label-cell">
                        <span className="bg-measurement-date-header-label">Measurement</span>
                        <br />
                        <span className="bg-measurement-date-header-label">Date</span>
                      </th>
                      {periodNames.map((periodName, periodIndex) => {
                        const periodInfo = getPeriodInfo(periodName);
                        const timeRange = periodInfo && periodInfo.startTimeDisplay && periodInfo.endTimeDisplay
                          ? `${periodInfo.startTimeDisplay} ~ ${periodInfo.endTimeDisplay}`
                          : '';
                        return (
                          <th key={`${bucket.id}-${periodName}`} colSpan="2" className="bg-table-header-cell" style={{ backgroundColor: getPeriodColor(periodIndex) }}>
                            {periodName}
                            {timeRange && (
                              <>
                                <br /><span className="bg-time-text">{timeRange}</span>
                              </>
                            )}
                          </th>
                        );
                      })}
                    </tr>
                    <tr>
                      {periodNames.map(periodName => (
                        <React.Fragment key={`${bucket.id}-${periodName}-sub`}>
                          <th className="bg-table-sub-header">Blood Glucose</th>
                          <th className="bg-table-sub-header">Measure Time</th>
                        </React.Fragment>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {bucket.dates.map((date) => (
                      <tr key={`row-${bucket.id}-${date}`}>
                        <td className="bg-table-label-cell bg-measurement-date-cell">{formatDate(date)}</td>
                        {periodNames.map((periodName) => (
                          <React.Fragment key={`cell-${bucket.id}-${date}-${periodName}`}>
                            {renderPeriodMeasurementsCells(dateMeasurementsByPeriodMap[date]?.[periodName] || [], periodName)}
                          </React.Fragment>
                        ))}
                      </tr>
                    ))}
                    <tr className="bg-average-row">
                      <td className="bg-table-label-cell">Average</td>
                      {periodNames.map((periodName) => {
                        const periodMeasurements = [];
                        bucket.dates.forEach((date) => {
                          const rows = dateMeasurementsByPeriodMap[date]?.[periodName] || [];
                          periodMeasurements.push(...rows);
                        });
                        const bgValues = periodMeasurements
                          .map((m) => parseFloat(m.blood_glucose_value_1 || m.blood_glucose_value))
                          .filter((v) => !isNaN(v));
                        return (
                          <React.Fragment key={`avg-${bucket.id}-${periodName}`}>
                            <td style={{ fontWeight: 600 }}>
                              {bgValues.length > 0 ? Math.round(bgValues.reduce((a, b) => a + b, 0) / bgValues.length) : '-'}
                            </td>
                            <td></td>
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  </tbody>
                </table>
              </div>
            );
          })
        ) : (
          <div className="bg-measurement-table-section bg-section-table">
            <table className="bg-measurement-table">
              <tbody>
                <tr>
                  <td colSpan={periodNames.length * 2 + 1} className="bg-table-label-cell" style={{ textAlign: 'center', padding: '20px' }}>
                    No measurement data available
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
        </>
        )}
      </div>

      {/* Summary Statistics Table */}
      <div className="bg-outline-section bg-summary-section">
        <h4 className="bg-outline-title">Summary Of Measurement</h4>
        {queryLoading || awaitingTargetsForColoring ? (
          <PageLoadingSpinner minHeight="240px" />
        ) : (
        <div className="bg-measurement-table-section bg-section-table">
          <table className="bg-measurement-table">
          <thead>
            <tr>
              <th rowSpan="2" className="bg-table-label-cell bg-summary-stat-cell">Statistics</th>
              {summaryHeaderGroups.map((group) => (
                group.items.length === 1 && group.items[0].isSingleHeader ? (
                  <th
                    key={group.group}
                    rowSpan="2"
                    className={`bg-table-header-cell bg-summary-group-header bg-single-period-col ${normalizePeriodName(group.group) === 'wakeup' ? 'bg-wakeup-col' : ''}`}
                  >
                    {group.group}
                  </th>
                ) : (
                  <th key={group.group} colSpan={group.items.length} className="bg-table-header-cell bg-summary-group-header">
                    {group.group}
                  </th>
                )
              ))}
            </tr>
            <tr>
              {summaryHeaderGroups.flatMap((group) =>
                group.items.map((item) => (
                  item.isSingleHeader ? null : (
                    <th key={`${group.group}-${item.periodName}`} className="bg-table-sub-header bg-summary-sub-header">
                      {item.sub}
                      {(() => {
                        const periodInfo = getPeriodInfo(item.periodName);
                        const timeRange = periodInfo?.startTimeDisplay && periodInfo?.endTimeDisplay
                          ? `${periodInfo.startTimeDisplay} ~ ${periodInfo.endTimeDisplay}`
                          : '';
                        if (!timeRange) return null;
                        return (
                          <>
                            <br />
                            <span className="bg-time-text">{timeRange}</span>
                          </>
                        );
                      })()}
                    </th>
                  )
                ))
              )}
            </tr>
          </thead>
          <tbody>
            {['Average', 'Maximum', 'Minimum', 'Count', 'Above Target', 'Below Target', 'Within Target'].map(statType => (
              <tr key={statType}>
                <td className="bg-table-label-cell bg-summary-stat-cell">{statType}</td>
                {summaryPeriodColumns.map((col) => {
                  const stats = calculatePeriodStats(effectiveMeasurements, col.periodName);
                  const cellClass = col.isSingleHeader ? 'bg-single-period-col' : '';
                  if (statType === 'Average') return <td key={`${col.periodName}-avg`} className={cellClass}>{stats.avg ? Math.round(stats.avg) : ''}</td>;
                  if (statType === 'Maximum') return <td key={`${col.periodName}-max`} className={cellClass}>{stats.max ? Math.round(stats.max) : ''}</td>;
                  if (statType === 'Minimum') return <td key={`${col.periodName}-min`} className={cellClass}>{stats.min ? Math.round(stats.min) : ''}</td>;
                  if (statType === 'Count') return <td key={`${col.periodName}-count`} className={cellClass}>{stats.count}</td>;
                  if (statType === 'Above Target') return <td key={`${col.periodName}-above`} className={cellClass}>{stats.above}</td>;
                  if (statType === 'Below Target') return <td key={`${col.periodName}-below`} className={cellClass}>{stats.below}</td>;
                  if (statType === 'Within Target') return <td key={`${col.periodName}-within`} className={cellClass}>{stats.within}</td>;
                  return <td key={`${col.periodName}-empty`} className={cellClass}></td>;
                })}
              </tr>
            ))}
            <tr className="bg-total-row">
              <td className="bg-table-label-cell bg-summary-stat-cell">Total</td>
              <td colSpan={totalRowLayout.beforeMealCols} className="bg-total-summary-cell">Below Target: {formatPercentCount(rangeTotals.below, totalReadingCount)}</td>
              <td colSpan={totalRowLayout.afterMealCols} className="bg-total-summary-cell">Above Target: {formatPercentCount(rangeTotals.above, totalReadingCount)}</td>
              <td colSpan={totalRowLayout.remainingCols} className="bg-total-summary-cell">Within Target: {formatPercentCount(rangeTotals.within, totalReadingCount)}</td>
            </tr>
          </tbody>
          </table>
        </div>
        )}
      </div>

      {/* Add Blood Glucose Modal */}
      {!practiceIsLocked && showAddModal && (
        <AddBGForm
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          practiceId={practiceId}
          patientId={patientId}
          initialScheduleTargets={scheduleTargets}
          onSuccess={handleAddDataSuccess}
        />
      )}

      {!practiceIsLocked && showFollowUpModal && (
        <AddFollowUpModal
          isOpen={showFollowUpModal}
          onClose={() => setShowFollowUpModal(false)}
          practiceId={practiceId}
          patientId={patientId}
          patientName={patientNameLine}
          initialTimerValue={timer}
          onSuccess={() => {}}
        />
      )}
      </>
      </div>{/* end bg-report-content */}

      <EmailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        defaultSubject={defaultEmailSubject}
        defaultContent={defaultEmailContent}
        onSend={async (emailData) => {
          try {
            if (emailData.attachments?.length) {
              toast.info('Attachments are not supported yet. Sending without attachments.');
            }
            setEmailSending(true);
            const payload = {
              to: emailData.to,
              cc: emailData.cc,
              bcc: emailData.bcc,
              subject: emailData.subject,
              text: emailData.content,
              html: `<pre style="font-family: Arial, sans-serif; white-space: pre-wrap;">${String(emailData.content || '').replace(/</g, '&lt;')}</pre>`,
              event_id: 10
            };
            const res = await emailService.sendCustomNotification(payload);
            if (!res.success) throw new Error(res.error || res.message);
            toast.success('Email queued successfully');
            setShowEmailModal(false);
          } catch (e) {
            toast.error(e?.message || 'Failed to send email');
          } finally {
            setEmailSending(false);
          }
        }}
      />
    </div>
  );
};

export default BloodGlucose;

